import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { Role } from '../model/role';
import { AptCawsAdminComponent } from './components/admin/apt-caws.component';
import { BusinessLinesAdminComponent } from './components/admin/business-lines.component';
import { ClientsAdminComponent } from './components/admin/clients.component';
import { ImpersonateAdminComponent } from './components/admin/impersonate.component';
import { IndustriesAdminComponent } from './components/admin/industries.component';
import { InspectionTeamRolesAdminComponent } from './components/admin/inspection-team-roles.component';
import { OfficesAdminComponent } from './components/admin/offices.component';
import { ProjectsAdminComponent } from './components/admin/projects.component';
import { TopicsAndStandardsComponent } from './components/admin/topics-and-standards.component';
import { StandardQuestionOptionsAdminComponent } from './components/admin/standard-question-options.component';
import { TagsAdminComponent } from './components/admin/tags.component';
import { NoLoginFoundComponent } from './components/error/not-active.component';
import { MyInspectionsComponent } from './components/home/data-grids/home.component';
import { MyInspectionComponent } from './components/home/forms/inspection.component';
import { AccessDeniedComponent } from './components/shared/access-denied/access-denied.component';
import { HomeScreenComponent } from './components/home-screen/home-screen.component';
import { InspectionEngagementsComponent } from './components/inspector-setup/data-grids/inspection-engagements.component';
import { InspectionEngagementEditorComponent } from './components/inspector-setup/forms/inspection-engagement-editor.component';
import { SignoutComponent } from './components/signout/signout-component';
import { ExportQuestionsAndResponsesComponent } from './components/reports/export-questions-and-responses.component';
import { AppGuard } from './guards/app-guard/app.guard';
import { RoleGuard } from './guards/auth/role-guard';
import { GenericCanDeactivateGuard } from './guards/generic-can-deactivate/generic-can-deactivate.guard';
import { SecurityRolesAdminComponent } from './components/admin/security-roles.component';
import { SpecialtyReviewerTypesAdminComponent } from './components/admin/specialty-reviewer-types.component';
import { InspectionQuestionsComponent } from './components/admin/inspection-questions.component';
import { InspectionQuestionEditorComponent } from './components/admin/inspection-question-editor.component';
import { QuestionSortingComponent } from './components/admin/question-sorting.component';
import { MyAssignmentsHomeComponent } from './components/my-assignments/home/my-assignments-home.component';
import { MyAssignmentInspectionComponent } from './components/my-assignments/home/my-assignment-inspection.component';

const routes: Routes = [
    {
        path: '',
        canActivate: [AppGuard],
        children: [
            {
                path: 'admin',
                canActivate: [RoleGuard],
                data: { order: 5, name: 'Admin', requiresAdmin: true, roles: [Role.admin] },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'business-lines'
                    },
                    {
                        path: 'apt-caws',
                        component: AptCawsAdminComponent
                    },
                    {
                        path: 'business-lines',
                        component: BusinessLinesAdminComponent
                    },
                    {
                        path: 'clients',
                        component: ClientsAdminComponent
                    },
                    {
                        path: 'industries',
                        component: IndustriesAdminComponent
                    },
                    {
                        path: 'offices',
                        component: OfficesAdminComponent
                    },
                    {
                        path: 'projects',
                        component: ProjectsAdminComponent
                    },
                    {
                        path: 'topics-and-standards',
                        component: TopicsAndStandardsComponent
                    },
                    {
                        path: 'questions',
                        component: InspectionQuestionsComponent
                    },
                    {
                        path: 'question-sorting',
                        component: QuestionSortingComponent
                    },
                    {
                        path: 'question',
                        component: InspectionQuestionEditorComponent,
                        canDeactivate: [GenericCanDeactivateGuard]
                    },
                    {
                        path: 'standard-question-options',
                        component: StandardQuestionOptionsAdminComponent
                    },
                    {
                        path: 'inspection-team-roles',
                        component: InspectionTeamRolesAdminComponent
                    },
                    {
                        path: 'tags',
                        component: TagsAdminComponent
                    },
                    {
                        path: 'impersonate',
                        component: ImpersonateAdminComponent
                    },
                    {
                        path: 'security-roles',
                        component: SecurityRolesAdminComponent
                    },
                    {
                        path: 'specialty-reviewer-types',
                        component: SpecialtyReviewerTypesAdminComponent
                    }
                ]
            },
            {
                path: 'reports',
                canActivate: [RoleGuard],
                data: { order: 5, name: 'Reports', requiresAdmin: true, roles: [Role.admin] },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'export-questions-and-responses'
                    },
                    {
                        path: 'export-questions-and-responses',
                        component: ExportQuestionsAndResponsesComponent
                    }
                ]
            },
            {
                path: 'inspector-setup',
                canActivate: [RoleGuard],
                data: { order: 2, name: 'Inspection Code Setup', requiresQcAdmin: true, roles: [Role.nationalQcAdmin, Role.admin] },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'inspection-engagements'
                    },
                    {
                        path: 'inspection-engagements',
                        component: InspectionEngagementsComponent
                    },
                    {
                        path: 'inspection-engagement',
                        component: InspectionEngagementEditorComponent,
                        canDeactivate: [GenericCanDeactivateGuard]
                    }
                ]
            },
            {
                path: 'home',
                canActivate: [RoleGuard],
                data: { order: 0, name: 'Home', requiresAdmin: false, requiresQcAdmin: false, roles: [Role.basic, Role.nationalQcAdmin, Role.admin] },
                component: HomeScreenComponent
            },
            {
                path: 'my-assignments',
                canActivate: [RoleGuard],
                data: { order: 1, name: 'My Assignments', requiresAdmin: false, requiresQcAdmin: false, roles: [Role.basic, Role.nationalQcAdmin, Role.admin] },
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        component: MyAssignmentsHomeComponent
                    },
                    {
                        path: 'inspection',
                        component: MyAssignmentInspectionComponent,
                        canDeactivate: [GenericCanDeactivateGuard]
                    }
                ]
            },
            { path: '', redirectTo: 'home', pathMatch: 'full' }
        ]
    },
    { path: 'access-denied', component: AccessDeniedComponent },
    { path: 'signout', component: SignoutComponent },
    { path: 'no-login-found', component: NoLoginFoundComponent },
    { path: '**', redirectTo: 'home', pathMatch: 'full' },
]

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule { }
